import { useContext, useEffect, useMemo, useState } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import {
  useSetFormValues,
  useSortedMainCoinsData,
} from "@shared/features/bots/BotForm/common/hooks";
import { getBotValues } from "@shared/features/bots/BotForm/common/utils";
import useBotsRestoreChanges from "@shared/features/bots/BotForm/common/hooks/useBotsRestoreChanges";
import { usePrevious } from "@shared/utils/hooks/misc";
import { BotFormType } from "@shared/features/bots/BotForm/common/types";
import useBotFormDefaultValues from "@shared/features/bots/BotForm/useBotFormDefaultValues";

const useBotSummaryTabsValuesProvider = () => {
  const {
    botFormType,
    botType,
    subGroupBotConfiguration,
    initialized: botFormInitialized,
    initializeFormValues,
    isLoading,
    backtestBotData,
    symbolsMetadata,
    publicBotGeneralData,
  } = useContext(BotFormContext)!;
  const [backtestingValuesModified, setBacktestingValuesModified] =
    useState(false);
  const restoreDefaultValues = useBotFormDefaultValues();
  const setFormValues = useSetFormValues();
  const prevBacktestBotData = usePrevious(backtestBotData);
  const prevPublicBotGeneralData = usePrevious(publicBotGeneralData);
  const signalsMainCoinsData = useSortedMainCoinsData();

  const [subGroupTemplateValues, subGroupTemplateCheckedFields] =
    useMemo(() => {
      if (
        subGroupBotConfiguration &&
        "options" in subGroupBotConfiguration &&
        botFormInitialized
      ) {
        return getBotValues(botType, subGroupBotConfiguration, {
          getExchangeSymbols: symbolsMetadata?.getExchangeSymbols,
          getAccountActiveSymbols: symbolsMetadata?.getAccountActiveSymbols,
        });
      }
      return [];
    }, [subGroupBotConfiguration, botFormInitialized]);

  const { tabToRestoreChangesData } = useBotsRestoreChanges({
    setFormValues,
    templateValues: subGroupTemplateValues,
    templateCheckedFields: subGroupTemplateCheckedFields,
  });

  useEffect(() => {
    if (subGroupTemplateValues && botFormInitialized) {
      initializeFormValues(subGroupBotConfiguration);
    }
  }, [botFormInitialized, subGroupBotConfiguration]);

  useEffect(() => {
    if (
      botFormType === BotFormType.CREATE &&
      botFormInitialized &&
      !isLoading &&
      ((prevBacktestBotData && !backtestBotData) ||
        (prevPublicBotGeneralData && !publicBotGeneralData))
    ) {
      restoreDefaultValues();
    }
  }, [backtestBotData, botFormInitialized, isLoading, publicBotGeneralData]);

  return {
    tabToRestoreChangesData,
    backtestingValuesModified,
    setBacktestingValuesModified,
    signalsMainCoinsData,
  };
};

export default useBotSummaryTabsValuesProvider;
